import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import Header from "../generic/Header";
import AdminModalTailwind from "./AdminModalTailwind";
import { groupsService, categoriesService, ageGroupsService } from '../../services';
import cacheService from '../../utils/cacheService';
import mockParticipants from '../../mockData/mockParticipants';
import { getAgeGroupColors, sortAgeGroups } from "../../utils/ageGroupColors";

const GroupsTailwind = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("all");
  const [selectedGender, setSelectedGender] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [selectedParticipants, setSelectedParticipants] = useState("all");
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal States für Startliste
  const [showStartListModal, setShowStartListModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupParticipants, setGroupParticipants] = useState([]);
  const [isStartListEditMode, setIsStartListEditMode] = useState(false);
  const [isSavingStartList, setIsSavingStartList] = useState(false);
  const [participantOrder, setParticipantOrder] = useState([]);
  const [expandedNations, setExpandedNations] = useState(new Set()); // Für Team-Startliste: ausklappbare Nationen
  const [teamOrder, setTeamOrder] = useState([]); // Für Team-Startreihenfolge
  
  // Toast State
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });

  // Toast Handler
  const addToast = (type, message) => {
    setToast({ show: true, message, type });
    
    // Toast nach 5 Sekunden automatisch entfernen
    setTimeout(() => {
      setToast({ show: false, message: "", type: "info" });
    }, 5000);
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  // Lade Daten beim ersten Laden
  useEffect(() => {
    loadData();
  }, []);

  // Lese Kategorie aus URL-Parameter und setze Filter
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Prüfe Cache für Kategorien und Altersgruppen
      const cachedCategories = cacheService.get('categories');
      const cachedAgeGroups = cacheService.get('ageGroups');

      // Lade Gruppen (immer), Kategorien und Altersgruppen nur wenn nicht gecacht
      const loadPromises = [groupsService.getAllGroupsWithFallback()];
      let categoriesIndex = -1;
      let ageGroupsIndex = -1;
      
      if (!cachedCategories) {
        categoriesIndex = loadPromises.length;
        loadPromises.push(categoriesService.getAllCategories());
      }
      if (!cachedAgeGroups) {
        ageGroupsIndex = loadPromises.length;
        loadPromises.push(ageGroupsService.getAllAgeGroups());
      }

      const responses = await Promise.all(loadPromises);
      const groupsResponse = responses[0];
      
      // Verwende Cache oder Response
      let categoriesResponse = cachedCategories 
        ? { success: true, data: cachedCategories } 
        : (categoriesIndex >= 0 ? responses[categoriesIndex] : null);
      let ageGroupsResponse = cachedAgeGroups 
        ? { success: true, data: cachedAgeGroups } 
        : (ageGroupsIndex >= 0 ? responses[ageGroupsIndex] : null);

      let allGroups = [];

      if (groupsResponse.success) {
        // Stelle sicher, dass data ein Array ist (kann auch ein leeres Array sein)
        const groupsData = Array.isArray(groupsResponse.data) ? groupsResponse.data : [];
        
        // Falls die Backend-API die Teilnehmeranzahl nicht dynamisch berechnet,
        // berechnen wir sie hier im Frontend
        const groupsWithCount = groupsData.map(group => {
          // Wenn currentParticipants bereits korrekt ist, verwende es
          if (group.currentParticipants !== undefined && group.currentParticipants !== null) {
            return group;
          }
          
          // Ansonsten setze es auf 0 (wird später dynamisch berechnet)
          return {
            ...group,
            currentParticipants: 0
          };
        });
        
        allGroups = groupsWithCount;
      } else {
        setError(groupsResponse.error || 'Fehler beim Laden der Gruppen');
      }

      // Team-Gruppe wird jetzt über getAllGroups geladen (ist eine echte DB-Gruppe)
      // Keine separate Ladung mehr nötig

      setGroups(allGroups);

      if (categoriesResponse && categoriesResponse.success) {
        setCategories(categoriesResponse.data);
        if (!cachedCategories) {
          cacheService.set('categories', categoriesResponse.data);
        }
      } else if (categoriesResponse) {
        setError(categoriesResponse.error);
      }

      if (ageGroupsResponse && ageGroupsResponse.success) {
        setAgeGroups(ageGroupsResponse.data);
        if (!cachedAgeGroups) {
          cacheService.set('ageGroups', ageGroupsResponse.data);
        }
      } else if (ageGroupsResponse) {
        setError(ageGroupsResponse.error);
      }
    } catch (err) {
      const errorMessage = "Fehler beim Laden der Daten";
      setError(errorMessage);
      console.error("Fehler beim Laden der Daten:", err);
    } finally {
      setLoading(false);
    }
  };

  // Event Handlers
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const handleAgeGroupChange = (ageGroup) => {
    setSelectedAgeGroup(ageGroup);
  };

  const handleGenderChange = (gender) => {
    setSelectedGender(gender);
  };

  const handleLocationChange = (location) => {
    setSelectedLocation(location);
  };

  const handleParticipantsChange = (participants) => {
    setSelectedParticipants(participants);
  };

  const handleViewStartList = async (group) => {
    setSelectedGroup(group);
    
    // Spezielle Behandlung für Team-Gruppe
    if (group.isTeamCompetition || group.id === 'team-competition') {
      // Lade Team-Gruppe neu für aktuelle Daten
      try {
        const teamGroupResponse = await groupsService.getTeamCompetitionGroup();
        if (teamGroupResponse.success && teamGroupResponse.data) {
          setSelectedGroup(teamGroupResponse.data);
          // Setze keine einzelnen Teilnehmer, sondern Teams-Struktur
          setGroupParticipants([]); // Wird nicht verwendet bei Teams
        }
      } catch (error) {
        console.error('Fehler beim Laden der Team-Gruppe:', error);
      }
      setShowStartListModal(true);
      return;
    }
    
    try {
      // Versuche echte Teilnehmerdaten mit Startnummern aus der Backend-API zu laden
      const startOrderResponse = await groupsService.getGroupWithStartOrder(group.id);
      
      if (startOrderResponse.success) {
        // Verwende die echten Daten aus der API
        setGroupParticipants(startOrderResponse.data.participants || []);
      } else {
        // Fallback: Versuche echte Teilnehmerdaten aus der Backend-API zu laden
        const participantsResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/participants`);
        
        if (participantsResponse.ok) {
          const participantsData = await participantsResponse.json();
          
          if (participantsData.success) {
            // Filtere Teilnehmer basierend auf der Gruppe
            const categoryName = group.category?.name || group.category;
            const ageGroupName = group.ageGroup?.name || group.ageGroup;
            
            const filteredParticipants = participantsData.data.filter(p => {
              // Prüfe ob Teilnehmer in der passenden Kategorie ist
              const hasMatchingCategory = p.categories && p.categories.some(cat => 
                cat.name === categoryName || cat === categoryName
              );
              
              // Prüfe Altersgruppe und Geschlecht
              const hasMatchingAgeGroup = p.ageGroup?.name === ageGroupName;
              const hasMatchingGender = p.gender.toLowerCase() === group.gender.toLowerCase();
              
              return hasMatchingCategory && hasMatchingAgeGroup && hasMatchingGender;
            });
            
            setGroupParticipants(filteredParticipants);
          } else {
            // Fallback zu Mock-Daten
            console.warn('API-Fehler, verwende Mock-Daten:', participantsData.message);
            const mockFiltered = mockParticipants.filter(p =>
              p.category.includes(group.category?.name || group.category) &&
              p.ageGroup === (group.ageGroup?.name || group.ageGroup) &&
              p.gender.toLowerCase() === group.gender.toLowerCase()
            );
            setGroupParticipants(mockFiltered);
          }
        } else {
          // Fallback zu Mock-Daten bei HTTP-Fehler
          console.warn('HTTP-Fehler, verwende Mock-Daten');
          const mockFiltered = mockParticipants.filter(p =>
            p.category.includes(group.category?.name || group.category) &&
            p.ageGroup === (group.ageGroup?.name || group.ageGroup) &&
            p.gender.toLowerCase() === group.gender.toLowerCase()
          );
          setGroupParticipants(mockFiltered);
        }
      }
    } catch (error) {
      // Fallback zu Mock-Daten bei Netzwerkfehler
      console.warn('Netzwerkfehler, verwende Mock-Daten:', error);
      const mockFiltered = mockParticipants.filter(p =>
        p.category.includes(group.category?.name || group.category) &&
        p.ageGroup === (group.ageGroup?.name || group.ageGroup) &&
        p.gender.toLowerCase() === group.gender.toLowerCase()
      );
      setGroupParticipants(mockFiltered);
    }
    
    setShowStartListModal(true);
  };

  const handleCloseStartListModal = () => {
    setShowStartListModal(false);
    setSelectedGroup(null);
    setGroupParticipants([]);
    setIsStartListEditMode(false);
    setParticipantOrder([]);
    setExpandedNations(new Set());
  };

  const handleToggleNationExpansion = (nationId) => {
    setExpandedNations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nationId)) {
        newSet.delete(nationId);
      } else {
        newSet.add(nationId);
      }
      return newSet;
    });
  };

  const handleToggleStartListEditMode = () => {
    if (isStartListEditMode) {
      // Beim Verlassen des Edit-Modus alle Änderungen verwerfen
      setParticipantOrder([]);
      setTeamOrder([]);
    } else {
      // Beim Aktivieren des Edit-Modus die aktuelle Reihenfolge setzen
      if (selectedGroup?.isTeamCompetition && selectedGroup.teams) {
        // Für Teams: Startnummern aus Teams übernehmen oder initialisieren
        setTeamOrder(selectedGroup.teams.map((team, index) => ({
          nationId: team.nationId,
          startNumber: team.startNumber || (index + 1)
        })));
      } else {
        setParticipantOrder([...groupParticipants]);
      }
    }
    setIsStartListEditMode(!isStartListEditMode);
  };

  const handleSaveStartListChanges = async () => {
    setIsSavingStartList(true);
    try {
      // Für Team-Gruppen: Speichere Team-Reihenfolge
      if (selectedGroup?.isTeamCompetition) {
        const response = await groupsService.updateTeamStartOrder(teamOrder);
        
        if (response.success) {
          // Aktualisiere die Team-Gruppe mit neuen Startnummern
          const updatedGroup = { ...selectedGroup };
          updatedGroup.teams = updatedGroup.teams.map(team => {
            const orderItem = teamOrder.find(to => to.nationId === team.nationId);
            return {
              ...team,
              startNumber: orderItem?.startNumber || null
            };
          }).sort((a, b) => {
            const aStart = a.startNumber || 999;
            const bStart = b.startNumber || 999;
            return aStart - bStart;
          });
          setSelectedGroup(updatedGroup);
          setIsStartListEditMode(false);
          setTeamOrder([]);
          
          // Lade Daten neu
          await loadData();
          
          addToast('success', 'Startreihenfolge der Teams erfolgreich gespeichert');
        } else {
          addToast('error', response.error || 'Fehler beim Speichern der Startreihenfolge');
          console.error("Fehler beim Speichern:", response.error);
        }
      } else {
        // Für normale Gruppen: Speichere Teilnehmer-Reihenfolge
        const startOrder = participantOrder.map((participant, index) => ({
          participantId: participant.id,
          startNumber: index + 1
        }));

        const response = await groupsService.updateStartOrder(selectedGroup.id, startOrder);
        
        if (response.success) {
          setGroupParticipants(participantOrder);
          setIsStartListEditMode(false);
          setParticipantOrder([]);
          
          addToast('success', 'Startreihenfolge erfolgreich gespeichert');
        } else {
          addToast('error', response.error || 'Fehler beim Speichern der Startreihenfolge');
          console.error("Fehler beim Speichern:", response.error);
        }
      }
    } catch (error) {
      addToast('error', 'Fehler beim Speichern der Startreihenfolge');
      console.error("Fehler beim Speichern:", error);
    } finally {
      setIsSavingStartList(false);
    }
  };

  const handleCancelStartListEdit = () => {
    setParticipantOrder([]);
    setTeamOrder([]);
    setIsStartListEditMode(false);
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const fromIndex = Number(e.dataTransfer.getData('text/plain'));
    if (fromIndex === targetIndex) return;

    const newOrder = [...participantOrder];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(targetIndex, 0, removed);
    setParticipantOrder(newOrder);
  };

  const getNationName = (participant) => {
    // Unterstütze sowohl Backend-API als auch Mock-Daten-Struktur
    let nationCode = 'DE'; // Fallback
    
    if (participant.nation && typeof participant.nation === 'object') {
      // Backend-API Struktur: participant.nation = { name: "Deutschland", code: "DE" }
      nationCode = participant.nation.code || 'DE';
    } else if (typeof participant.nationCode === 'string') {
      // Mock-Daten Struktur: participant.nationCode = "DE"
      nationCode = participant.nationCode;
    } else if (typeof participant.nation === 'string') {
      // Alternative Mock-Daten Struktur: participant.nation = "Deutschland"
      // Versuche den Code aus dem Namen zu extrahieren
      const nationNames = {
        "Deutschland": "DE",
        "Schweiz": "CH", 
        "Österreich": "AT",
        "China": "CN",
        "Japan": "JP"
      };
      nationCode = nationNames[participant.nation] || 'DE';
    }
    
    const nations = {
      "DE": "Deutschland",
      "CH": "Schweiz",
      "AT": "Österreich",
      "CN": "China",
      "JP": "Japan"
    };
    return nations[nationCode] || nationCode;
  };

  const getNationCode = (participant) => {
    // Unterstütze sowohl Backend-API als auch Mock-Daten-Struktur
    if (participant.nation && typeof participant.nation === 'object') {
      // Backend-API Struktur: participant.nation = { name: "Deutschland", code: "DE" }
      return participant.nation.code || 'DE';
    } else if (typeof participant.nationCode === 'string') {
      // Mock-Daten Struktur: participant.nationCode = "DE"
      return participant.nationCode;
    } else if (typeof participant.nation === 'string') {
      // Alternative Mock-Daten Struktur: participant.nation = "Deutschland"
      const nationNames = {
        "Deutschland": "DE",
        "Schweiz": "CH", 
        "Österreich": "AT",
        "China": "CN",
        "Japan": "JP"
      };
      return nationNames[participant.nation] || 'DE';
    }
    return 'DE'; // Fallback
  };

  const handleViewResults = (group) => {
    const params = new URLSearchParams();
    
    // Verwende die spezifischen Werte der ausgewählten Gruppe
    if (group.category?.name || group.category) {
      params.append('category', group.category?.name || group.category);
    }
    if (group.ageGroup?.name || group.ageGroup) {
      params.append('ageGroup', group.ageGroup?.name || group.ageGroup);
    }
    
    // Konvertiere Geschlechtswerte für Results.js
    if (group.gender) {
      params.append('gender', group.gender);
    }
    
    if (group.venue) {
      params.append('location', group.venue);
    }
    
    navigate(`/admin/results?${params.toString()}`);
  };

  const categoryOptions = [
    { value: "all", label: t("admin.groups.allCategories", "Alle Kategorien") },
    ...categories.map((category) => ({
      value: category.name,
      label: category.name
    }))
  ];

  const statusOptions = [
    { value: "all", label: t("admin.groups.allStatus", "Alle Status") },
    { value: "Ausstehend", label: t("admin.groups.pending", "Ausstehend") },
    { value: "Aktiv", label: t("admin.groups.active", "Aktiv") },
    { value: "Abgeschlossen", label: t("admin.groups.completed", "Abgeschlossen") }
  ];

  const ageGroupOptions = [
    { value: "all", label: t("admin.groups.allAgeGroups", "Alle Altersgruppen") },
    ...ageGroups.map((ageGroup) => ({
      value: ageGroup.name,
      label: ageGroup.name
    }))
  ];

  const genderOptions = [
    { value: "all", label: t("admin.groups.allGenders", "Alle Geschlechter") },
    { value: "männlich", label: t("admin.groups.male", "Männlich") },
    { value: "weiblich", label: t("admin.groups.female", "Weiblich") },
    { value: "gemischt", label: t("admin.groups.mixed", "Gemischt") },
  ];

  const locationOptions = [
    { value: "all", label: t("admin.groups.allLocations", "Alle Flächen") },
    { value: "Fläche A", label: "Fläche A" },
    { value: "Fläche B", label: "Fläche B" },
  ];

  const participantsOptions = [
    { value: "all", label: t("admin.groups.allParticipants", "Alle Teilnehmeranzahlen") },
    { value: "0", label: "0 Teilnehmer" },
    { value: "1-3", label: "1-3 Teilnehmer" },
    { value: "4-10", label: "4-10 Teilnehmer" },
    { value: "10+", label: "10+ Teilnehmer" }
  ];

  const getStatusLabel = (status) => {
    switch (status) {
      case "Ausstehend":
        return t("admin.groups.pending", "Ausstehend");
      case "Aktiv":
        return t("admin.groups.active", "Aktiv");
      case "Abgeschlossen":
        return t("admin.groups.completed", "Abgeschlossen");
      case "Vorbereitung":
        return t("admin.groups.preparation", "Vorbereitung");
      default:
        return status;
    }
  };

  const getCategoryName = (item) => {
    return item.category?.name || item.category || 'Unbekannt';
  };

  const getAgeGroupName = (item) => {
    return item.ageGroup?.name || item.ageGroup || 'Unbekannt';
  };

  // Funktion für kompakte Gruppennamen
  const getCompactGroupName = (group) => {
    const categoryName = getCategoryName(group);
    const ageGroupName = getAgeGroupName(group);
    const gender = group.gender;
    
    // Kategorie-Abkürzung (erste Buchstaben)
    const getCategoryAbbreviation = (category) => {
      const words = category.split(' ');
      if (words.length === 1) {
        return category.substring(0, 3).toUpperCase();
      }
      return words.map(word => word.charAt(0)).join('').toUpperCase();
    };
    
    // Altersgruppen-Abkürzungen
    const getAgeGroupAbbreviation = (ageGroup) => {
      const abbreviations = {
        'Kinder': 'K',
        'Jugend': 'J',
        'Erwachsene': 'E',
        'Senioren': 'S',
        'Senioren Plus': 'SP',
        'Masters': 'M',
        'Veterans': 'V'
      };
      return abbreviations[ageGroup] || ageGroup.substring(0, 2).toUpperCase();
    };
    
    // Geschlecht-Abkürzung
    const getGenderAbbreviation = (gender) => {
      return gender === 'männlich' ? 'M' : 'W';
    };
    
    const categoryAbbr = getCategoryAbbreviation(categoryName);
    const ageGroupAbbr = getAgeGroupAbbreviation(ageGroupName);
    const genderAbbr = getGenderAbbreviation(gender);
    
    return `${categoryAbbr}-${ageGroupAbbr}-${genderAbbr}`;
  };

  const getParticipantCountForGroup = (group) => {
    // Berechne die Teilnehmeranzahl basierend auf den gefilterten Teilnehmern
    const categoryName = group.category?.name || group.category;
    const ageGroupName = group.ageGroup?.name || group.ageGroup;
    
    // Zähle die Teilnehmer, die zu dieser Gruppe gehören würden
    const matchingParticipants = mockParticipants.filter(p =>
      p.category.includes(categoryName) &&
      p.ageGroup === ageGroupName &&
      p.gender.toLowerCase() === group.gender.toLowerCase()
    );
    
    return matchingParticipants.length;
  };

  const filteredGroups = groups.filter((item) => {
    const isTeamGroup = item.name === 'Gruppenwettbewerb' || item.isTeamCompetition || item.id === 'team-competition';
    
    // Team-Gruppe wird wie normale Gruppen behandelt, aber mit spezieller Filterlogik
    const categoryName = item.category?.name || item.category || '';
    const ageGroupName = item.ageGroup?.name || item.ageGroup || '';
    const description = item.description || '';
    const compactName = getCompactGroupName(item);
    
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         categoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         compactName.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesCategory, matchesAgeGroup, matchesGender;
    
    if (isTeamGroup) {
      // Spezielle Filterlogik für Team-Gruppe
      matchesCategory = selectedCategory === "all" || selectedCategory === "Gruppenperformance";
      matchesAgeGroup = selectedAgeGroup === "all" || ageGroupName === selectedAgeGroup || ageGroupName === 'Alle';
      matchesGender = selectedGender === "all" || item.gender === selectedGender || (item.gender === 'gemischt' && selectedGender === 'gemischt');
    } else {
      // Normale Filterlogik
      matchesCategory = selectedCategory === "all" || categoryName === selectedCategory;
      matchesAgeGroup = selectedAgeGroup === "all" || ageGroupName === selectedAgeGroup;
      matchesGender = selectedGender === "all" || item.gender === selectedGender;
    }
    
    const matchesStatus = selectedStatus === "all" || item.status === selectedStatus;
    const matchesLocation = selectedLocation === "all" || (item.venue === selectedLocation);
    
    // Teilnehmeranzahl-Filter
    let matchesParticipants = true;
    if (selectedParticipants !== "all") {
      const participantCount = item.currentParticipants || 0;
      switch (selectedParticipants) {
        case "0":
          matchesParticipants = participantCount === 0;
          break;
        case "1-3":
          matchesParticipants = participantCount >= 1 && participantCount <= 3;
          break;
        case "4-10":
          matchesParticipants = participantCount >= 4 && participantCount <= 10;
          break;
        case "10+":
          matchesParticipants = participantCount > 10;
          break;
        default:
          matchesParticipants = true;
      }
    }
    
    return matchesSearch && matchesCategory && matchesStatus && matchesAgeGroup && matchesGender && matchesLocation && matchesParticipants;
  });

  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64 mb-2"></div>
            <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-96"></div>
          </div>

          {/* Controls Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
              <div className="flex gap-2">
                <div className="w-24 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
                <div className="w-24 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
              </div>
            </div>
          </div>

          {/* Filters Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="space-y-6">
              {[...Array(5)].map((_, i) => (
                <div key={i}>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24 mb-3"></div>
                  <div className="flex flex-wrap gap-1.5">
                    {[...Array(4)].map((_, j) => (
                      <div key={j} className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-24"></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Table Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    {[...Array(9)].map((_, i) => (
                      <th key={i} className="px-6 py-3">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {[...Array(8)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(9)].map((_, j) => (
                        <td key={j} className="px-6 py-4">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <p className="text-slate-600 dark:text-slate-400 font-medium mb-4">{error}</p>
                <button 
                  onClick={loadData} 
                  className="bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary text-white px-6 py-3 rounded-md transition-colors duration-200 font-medium"
                >
                  {t("admin.retry", "Erneut versuchen")}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("admin.groups.title", "Gruppenverwaltung")}
                </h1>
                <p className="text-slate-600 dark:text-slate-400 mt-2">
                  {t("admin.groups.subtitle", "Verwalte alle Gruppen und deren Zuordnung.")}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{filteredGroups.length}</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">Gruppen</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Controls Section */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder={t("admin.groups.search", "Suche nach Gruppe oder Kategorie...")}
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 h-[46px] border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                />
                <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm"></i>
              </div>

              {/* Add Button */}
              <button 
                className="bg-primary dark:bg-primary-light hover:bg-primary-dark dark:hover:bg-primary text-white px-4 h-[46px] rounded-md text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap"
              >
                <i className="fas fa-plus text-xs"></i>
                {t("admin.groups.add", "Neue Gruppe")}
              </button>
            </div>
          </div>

          {/* Filter Groups */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
            <div className="space-y-6">
              {/* Category Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Kategorie</h3>
                <div className="flex flex-wrap gap-1.5">
                  {categoryOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleCategoryChange(option.value)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedCategory === option.value
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Status</h3>
                <div className="flex flex-wrap gap-1.5">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleStatusChange(option.value)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedStatus === option.value
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Age Group Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Altersgruppe</h3>
                <div className="flex flex-wrap gap-1.5">
                  {ageGroupOptions.map((option) => {
                    const isSelected = selectedAgeGroup === option.value;
                    
                    if (option.value === "all") {
                      return (
                        <button
                          key={option.value}
                          onClick={() => handleAgeGroupChange(option.value)}
                          className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                            isSelected
                              ? 'bg-primary dark:bg-primary-light text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    }
                    
                    const colors = getAgeGroupColors(option.value);
                    
                    // Dark Mode Varianten für Altersgruppen-Badges
                    const getDarkModeClasses = (ageGroup) => {
                      const darkModeMap = {
                        'Kinder': 'dark:bg-emerald-900/30 dark:text-emerald-300',
                        'Jugend': 'dark:bg-blue-900/30 dark:text-blue-300',
                        'Erwachsene': 'dark:bg-violet-900/30 dark:text-violet-300',
                        'Senioren': 'dark:bg-amber-900/30 dark:text-amber-300',
                        'Senioren Plus': 'dark:bg-red-900/30 dark:text-red-300'
                      };
                      return darkModeMap[ageGroup] || 'dark:bg-slate-700 dark:text-slate-300';
                    };
                    
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleAgeGroupChange(option.value)}
                        className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                          isSelected
                            ? `${colors.bg} text-white`
                            : `${colors.bgLight} ${colors.text} ${getDarkModeClasses(option.value)} hover:${colors.bg} hover:text-white`
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Gender Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Geschlecht</h3>
                <div className="flex flex-wrap gap-1.5">
                  {genderOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleGenderChange(option.value)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedGender === option.value
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Location Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Fläche</h3>
                <div className="flex flex-wrap gap-1.5">
                  {locationOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleLocationChange(option.value)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedLocation === option.value
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Participants Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Teilnehmeranzahl</h3>
                <div className="flex flex-wrap gap-1.5">
                  {participantsOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleParticipantsChange(option.value)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedParticipants === option.value
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Table Section */}
          {filteredGroups.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-32">
                      {t("admin.groups.name", "Gruppe")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-40">
                      {t("admin.groups.category", "Kategorie")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-32">
                      {t("admin.groups.ageGroup", "Altersgruppe")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-32">
                      {t("admin.groups.gender", "Geschlecht")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-24">
                      {t("admin.groups.location", "Fläche")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-20">
                      {t("admin.groups.participants", "Teiln.")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-24">
                      {t("admin.groups.status", "Status")}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-20">
                      {t("admin.groups.actions", "Aktionen")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                  {filteredGroups.map((item) => {
                    const isTeamGroup = item.isTeamCompetition || item.id === 'team-competition';
                    
                    return (
                      <tr key={item.id} className={`hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors ${isTeamGroup ? 'bg-blue-50/30 dark:bg-blue-900/20' : ''}`}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono font-medium text-slate-900 dark:text-slate-100" title={item.name}>
                            {isTeamGroup ? item.name : getCompactGroupName(item)}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            isTeamGroup ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' :
                            item.category?.type === 'Handform' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                            item.category?.type === 'Waffenform' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                            item.category?.type === 'Offene Form' ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' :
                            item.category?.type === 'Partnerform' ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' :
                            'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                          }`} title={getCategoryName(item)}>
                            {getCategoryName(item)}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {isTeamGroup ? (
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                              (() => {
                                const ageGroupName = getAgeGroupName(item) || item.ageGroup?.name || 'Alle';
                                const colors = getAgeGroupColors(ageGroupName);
                                const darkModeMap = {
                                  'Kinder': 'dark:bg-emerald-900/30 dark:text-emerald-300',
                                  'Jugend': 'dark:bg-blue-900/30 dark:text-blue-300',
                                  'Erwachsene': 'dark:bg-violet-900/30 dark:text-violet-300',
                                  'Senioren': 'dark:bg-amber-900/30 dark:text-amber-300',
                                  'Senioren Plus': 'dark:bg-red-900/30 dark:text-red-300',
                                  'Alle': 'dark:bg-slate-700 dark:text-slate-300'
                                };
                                const darkModeClasses = darkModeMap[ageGroupName] || 'dark:bg-slate-700 dark:text-slate-300';
                                return `${colors.bgLight} ${colors.text} ${darkModeClasses}`;
                              })()
                            }`} title={getAgeGroupName(item) || item.ageGroup?.name || 'Alle'}>
                              {getAgeGroupName(item) || item.ageGroup?.name || 'Alle'}
                            </span>
                          ) : (
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                              (() => {
                                const ageGroupName = getAgeGroupName(item);
                                const colors = getAgeGroupColors(ageGroupName);
                                const darkModeMap = {
                                  'Kinder': 'dark:bg-emerald-900/30 dark:text-emerald-300',
                                  'Jugend': 'dark:bg-blue-900/30 dark:text-blue-300',
                                  'Erwachsene': 'dark:bg-violet-900/30 dark:text-violet-300',
                                  'Senioren': 'dark:bg-amber-900/30 dark:text-amber-300',
                                  'Senioren Plus': 'dark:bg-red-900/30 dark:text-red-300'
                                };
                                const darkModeClasses = darkModeMap[ageGroupName] || 'dark:bg-slate-700 dark:text-slate-300';
                                return `${colors.bgLight} ${colors.text} ${darkModeClasses}`;
                              })()
                            }`} title={getAgeGroupName(item)}>
                              {getAgeGroupName(item)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {isTeamGroup ? (
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                              item.gender === 'gemischt' ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' :
                              item.gender === 'männlich' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300'
                            }`} title={item.gender === "gemischt" ? "Gemischt" : item.gender === "männlich" ? t("admin.groups.male", "Männlich") : t("admin.groups.female", "Weiblich")}>
                              {item.gender === "gemischt" ? "Gemischt" : item.gender === "männlich" ? "Männlich" : "Weiblich"}
                            </span>
                          ) : (
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                              item.gender === 'männlich' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300'
                            }`} title={item.gender === "männlich" ? t("admin.groups.male", "Männlich") : t("admin.groups.female", "Weiblich")}>
                              {item.gender === "männlich" ? "Männlich" : "Weiblich"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {item.venue ? (
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                              item.venue === 'Fläche A' 
                                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' 
                                : item.venue === 'Fläche B'
                                ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                            }`}>
                              <i className={`fas ${item.venue === 'Fläche A' ? 'fa-map-marker-alt' : 'fa-map-marker-alt'} mr-1.5`}></i>
                              {item.venue}
                            </span>
                          ) : (
                            <span className="text-sm text-slate-400 dark:text-slate-500">-</span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {item.currentParticipants !== undefined ? item.currentParticipants : getParticipantCountForGroup(item)}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            item.status === 'Ausstehend' ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                            item.status === 'Aktiv' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                            item.status === 'Vorbereitung' ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' :
                            item.status === 'Abgeschlossen' ? 'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-300' :
                            'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                          }`} title={getStatusLabel(item.status)}>
                            <i className={`fas ${
                              item.status === 'Aktiv' ? 'fa-play' : 
                              item.status === 'Ausstehend' ? 'fa-clock' : 
                              item.status === 'Vorbereitung' ? 'fa-hourglass-half' :
                              'fa-check'
                            } mr-1.5`}></i>
                            {getStatusLabel(item.status)}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-1">
                            <button 
                              onClick={() => handleViewStartList(item)}
                              className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                              title={t("admin.groups.startList", "Startliste")}
                            >
                              <i className="fas fa-list text-sm"></i>
                            </button>
                            <button 
                              onClick={() => handleViewResults(item)}
                              className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                              title={t("admin.groups.results", "Ergebnisse")}
                            >
                              <i className="fas fa-trophy text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {/* Keine Gruppen gefunden Meldung */}
          {filteredGroups.length === 0 && !loading && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-layer-group text-slate-400 dark:text-slate-500 text-xl"></i>
              </div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">Keine Gruppen gefunden</h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
                {searchTerm || selectedCategory !== "all" || selectedStatus !== "all" || selectedAgeGroup !== "all" || selectedGender !== "all" || selectedLocation !== "all" || selectedParticipants !== "all"
                  ? "Versuchen Sie, Ihre Filter zu ändern oder den Suchbegriff anzupassen."
                  : "Um Gruppen zu erstellen, müssen zunächst Teilnehmer erstellt werden. Gruppen werden automatisch basierend auf den vorhandenen Teilnehmern generiert."
                }
              </p>
            </div>
          )}
          </div> 
      </main>

      {/* Startliste Modal */}
      {showStartListModal && (
        <AdminModalTailwind
          isOpen={showStartListModal}
          onClose={handleCloseStartListModal}
          title={selectedGroup ? (selectedGroup.isTeamCompetition ? `Startliste - ${selectedGroup.name}` : `Startliste - ${getCompactGroupName(selectedGroup)}`) : "Startliste"}
          subtitle={selectedGroup?.isTeamCompetition 
            ? `${selectedGroup.teams?.length || 0} Nationen mit ${selectedGroup.currentParticipants || 0} Teilnehmern`
            : selectedGroup ? `${groupParticipants.length} Teilnehmer in ${getCategoryName(selectedGroup)} (${getAgeGroupName(selectedGroup)}, ${selectedGroup.gender})` : ""
          }
          onSave={isStartListEditMode ? handleSaveStartListChanges : null}
          onCancel={isStartListEditMode ? handleCancelStartListEdit : null}
          saveButtonText={isStartListEditMode ? "Speichern" : null}
          cancelButtonText={isStartListEditMode ? "Abbrechen" : null}
          isSaving={isSavingStartList}
        >
          <div className="space-y-6">
            {selectedGroup && !selectedGroup.isTeamCompetition && (
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Kategorie:</span>
                    <div className="mt-1">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                        selectedGroup.category?.type === 'Handform' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                        selectedGroup.category?.type === 'Waffenform' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                        selectedGroup.category?.type === 'Offene Form' ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' :
                        selectedGroup.category?.type === 'Partnerform' ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' :
                        'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                      }`}>
                        {getCategoryName(selectedGroup)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Altersgruppe:</span>
                    <div className="mt-1">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                        (() => {
                          const ageGroupName = getAgeGroupName(selectedGroup);
                          const colors = getAgeGroupColors(ageGroupName);
                          const darkModeMap = {
                            'Kinder': 'dark:bg-emerald-900/30 dark:text-emerald-300',
                            'Jugend': 'dark:bg-blue-900/30 dark:text-blue-300',
                            'Erwachsene': 'dark:bg-violet-900/30 dark:text-violet-300',
                            'Senioren': 'dark:bg-amber-900/30 dark:text-amber-300',
                            'Senioren Plus': 'dark:bg-red-900/30 dark:text-red-300'
                          };
                          const darkModeClasses = darkModeMap[ageGroupName] || 'dark:bg-slate-700 dark:text-slate-300';
                          return `${colors.bgLight} ${colors.text} ${darkModeClasses}`;
                        })()
                      }`}>
                        {getAgeGroupName(selectedGroup)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Geschlecht:</span>
                    <div className="mt-1">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                        selectedGroup.gender === 'männlich' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300'
                      }`}>
                        {selectedGroup.gender === "männlich" ? t("admin.groups.male", "Männlich") : t("admin.groups.female", "Weiblich")}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Fläche:</span>
                    <div className="mt-1 text-sm text-slate-900 dark:text-slate-100">{selectedGroup.venue || "Nicht zugewiesen"}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Team-Gruppe Startliste */}
            {selectedGroup?.isTeamCompetition && selectedGroup.teams ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <i className="fas fa-users text-slate-500 dark:text-slate-400"></i>
                    Teams ({selectedGroup.teams.length} Nationen, {selectedGroup.currentParticipants || 0} Teilnehmer)
                  </h4>
                  <div className="flex items-center gap-2">
                    {isStartListEditMode ? (
                      <>
                        <button 
                          className="p-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                          title="Änderungen speichern"
                          onClick={handleSaveStartListChanges}
                          disabled={isSavingStartList}
                        >
                          <i className={`fas ${isSavingStartList ? 'fa-spinner fa-spin' : 'fa-save'} text-sm`}></i>
                        </button>
                        <button 
                          className="p-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                          title="Abbrechen"
                          onClick={handleCancelStartListEdit}
                          disabled={isSavingStartList}
                        >
                          <i className="fas fa-times text-sm"></i>
                        </button>
                      </>
                    ) : (
                      <button 
                        className="p-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                        title="Reihenfolge bearbeiten"
                        onClick={handleToggleStartListEditMode}
                      >
                        <i className="fas fa-edit text-sm"></i>
                      </button>
                    )}
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                  <div className="divide-y divide-slate-200 dark:divide-slate-700">
                    {(isStartListEditMode ? teamOrder.map(to => selectedGroup.teams.find(t => t.nationId === to.nationId)).filter(Boolean) : selectedGroup.teams).map((team) => {
                      const isExpanded = expandedNations.has(team.nationId);
                      const totalParticipants = team.participants?.length || 0;
                      const displayOrder = isStartListEditMode ? teamOrder.find(to => to.nationId === team.nationId) : { startNumber: team.startNumber };
                      
                      return (
                        <div 
                          key={team.nationId} 
                          className={`transition-colors ${isStartListEditMode ? 'cursor-move' : ''}`}
                          draggable={isStartListEditMode}
                          onDragStart={isStartListEditMode ? (e) => {
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', team.nationId.toString());
                          } : undefined}
                          onDragOver={isStartListEditMode ? (e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                          } : undefined}
                          onDrop={isStartListEditMode ? (e) => {
                            e.preventDefault();
                            const draggedNationId = parseInt(e.dataTransfer.getData('text/plain'));
                            if (draggedNationId === team.nationId) return;
                            
                            const newOrder = [...teamOrder];
                            const draggedIndex = newOrder.findIndex(to => to.nationId === draggedNationId);
                            const targetIndex = newOrder.findIndex(to => to.nationId === team.nationId);
                            
                            if (draggedIndex !== -1 && targetIndex !== -1) {
                              const [removed] = newOrder.splice(draggedIndex, 1);
                              newOrder.splice(targetIndex, 0, removed);
                              // Aktualisiere Startnummern
                              setTeamOrder(newOrder.map((item, index) => ({
                                ...item,
                                startNumber: index + 1
                              })));
                            }
                          } : undefined}
                        >
                          {/* Nationen-Header (ausklappbar) */}
                          <button
                            onClick={() => !isStartListEditMode && handleToggleNationExpansion(team.nationId)}
                            className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left"
                            disabled={isStartListEditMode}
                          >
                            <div className="flex items-center gap-3 flex-1">
                              {isStartListEditMode ? (
                                <i className="fas fa-grip-vertical text-slate-400 dark:text-slate-500"></i>
                              ) : (
                                <div className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                                  <i className="fas fa-chevron-right text-slate-400 dark:text-slate-500 text-sm"></i>
                                </div>
                              )}
                              {displayOrder?.startNumber && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary dark:bg-primary-light text-white">
                                  #{displayOrder.startNumber}
                                </span>
                              )}
                              <img
                                src={process.env.PUBLIC_URL + "/images/flags/" + team.nationCode + ".png"}
                                alt={team.nationName}
                                className="w-8 h-8 object-cover rounded-full border border-slate-200 dark:border-slate-700"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{team.nationName}</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">{totalParticipants} Teilnehmer</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                                <i className="fas fa-users text-[10px]"></i>
                                Team
                              </span>
                            </div>
                          </button>

                          {/* Ausklappbare Teilnehmer-Liste */}
                          {isExpanded && (
                            <div className="bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
                              <div className="px-6 py-4">
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                                    <thead className="bg-white dark:bg-slate-800">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">ID</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Name</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Alter</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Geschlecht</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-700">
                                      {team.participants.map((participant, index) => (
                                        <tr key={participant.id || index} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50">
                                          <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-slate-500 dark:text-slate-400">
                                            {participant.participantNumber || participant.id}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-100">
                                            {participant.name}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                                            {participant.age}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                                              participant.gender === 'männlich' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300'
                                            }`}>
                                              {participant.gender === 'männlich' ? 'Männlich' : 'Weiblich'}
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              /* Normale Gruppen-Startliste */
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <i className="fas fa-users text-slate-500 dark:text-slate-400"></i>
                    Teilnehmer ({groupParticipants.length})
                  </h4>
                  <div className="flex items-center gap-2">
                    {isStartListEditMode ? (
                      <>
                        <button 
                          className="p-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                          title="Änderungen speichern"
                          onClick={handleSaveStartListChanges}
                          disabled={isSavingStartList}
                        >
                          <i className={`fas ${isSavingStartList ? 'fa-spinner fa-spin' : 'fa-save'} text-sm`}></i>
                        </button>
                        <button 
                          className="p-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                          title="Abbrechen"
                          onClick={handleCancelStartListEdit}
                          disabled={isSavingStartList}
                        >
                          <i className="fas fa-times text-sm"></i>
                        </button>
                      </>
                    ) : (
                      <button 
                        className="p-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                        title="Reihenfolge bearbeiten"
                        onClick={handleToggleStartListEditMode}
                      >
                        <i className="fas fa-edit text-sm"></i>
                      </button>
                    )}
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                      <thead className="bg-slate-50 dark:bg-slate-800">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Startnr.</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Nation</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Alter</th>
                          {selectedGroup?.category?.type === 'Offene Form' && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Form-Name</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                        {(isStartListEditMode ? participantOrder : groupParticipants).map((participant, index) => (
                          <tr 
                            key={participant.id || index}
                            draggable={isStartListEditMode}
                            onDragStart={isStartListEditMode ? (e) => handleDragStart(e, index) : undefined}
                            onDragOver={isStartListEditMode ? handleDragOver : undefined}
                            onDrop={isStartListEditMode ? (e) => handleDrop(e, index) : undefined}
                            className={`${isStartListEditMode ? 'cursor-move hover:bg-slate-50 dark:hover:bg-slate-700' : ''} transition-colors`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary dark:bg-primary-light text-white">
                                #{participant.startNumber || (index + 1)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-500 dark:text-slate-400">
                              {participant.participantNumber || participant.id || `P${index + 1}`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-100">
                              {participant.firstName && participant.lastName 
                                ? `${participant.firstName} ${participant.lastName}`
                                : (typeof participant.name === 'string' ? participant.name : 'Unbekannt')
                              }
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <img
                                  src={process.env.PUBLIC_URL + "/images/flags/" + getNationCode(participant) + ".png"}
                                  alt={getNationName(participant)}
                                  className="w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-700"
                                />
                                <span className="text-sm text-slate-900 dark:text-slate-100">{getNationName(participant)}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                              {String(participant.age || 'N/A')}
                            </td>
                            {selectedGroup?.category?.type === 'Offene Form' && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                                {(() => {
                                  // 1) Falls Backend openFormName direkt liefert (aus Gruppen-Endpunkt)
                                  if (participant.openFormName && String(participant.openFormName).trim()) {
                                    return participant.openFormName;
                                  }
                                  // 2) Falls Teilnehmer Kategorien mit through-Attributen hat
                                  const cats = participant.categories || [];
                                  const match = cats.find(c => (c.name || c) === (selectedGroup.category?.name || selectedGroup.category));
                                  if (match && match.formName && String(match.formName).trim()) {
                                    return match.formName;
                                  }
                                  // 3) Fallback
                                  return '-';
                                })()}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </AdminModalTailwind>
      )}

      {/* Toast */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`px-4 py-3 rounded-lg border shadow-lg bg-white dark:bg-slate-800 ${
            toast.type === 'success' ? 'border-green-200 dark:border-green-800' :
            toast.type === 'error' ? 'border-red-200 dark:border-red-800' :
            'border-blue-200 dark:border-blue-800'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                toast.type === 'success' ? 'bg-green-100 dark:bg-green-900/30' :
                toast.type === 'error' ? 'bg-red-100 dark:bg-red-900/30' :
                'bg-blue-100 dark:bg-blue-900/30'
              }`}>
                <i className={`fas text-xs ${
                  toast.type === 'success' ? 'fa-check text-green-600 dark:text-green-400' :
                  toast.type === 'error' ? 'fa-times text-red-600 dark:text-red-400' :
                  'fa-info text-blue-600 dark:text-blue-400'
                }`}></i>
              </div>
              <span className={`text-sm font-medium ${
                toast.type === 'success' ? 'text-green-900 dark:text-green-300' :
                toast.type === 'error' ? 'text-red-900 dark:text-red-300' :
                'text-blue-900 dark:text-blue-300'
              }`}>{toast.message}</span>
              <button
                onClick={hideToast}
                className="ml-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GroupsTailwind;
